<template>
	<div class="grid-flex-container pa-4">
		<div class="text-container">
			<p class="mb-2" style="font-weight: bold">
				Welcome to the IRIS Form Builder
			</p>
			<p class="mb-2">
				To begin the form creation process, select the Add New button
				from the right-hand side of the screen.
			</p>
			<p class="mb-2" style="font-weight: bold">Steady-state forms</p>
			<p class="mb-2">
				Steady-state forms are designed and optimized for use in daily
				install-move-add-change (IMAC) operations in a steady-state IT
				environment.
			</p>
			<p class="mb-2" style="font-weight: bold">Inventory forms</p>
			<p class="mb-2">
				Inventory Forms are forms that are intended to be used with
				almost all aspects of IRIS data gathering. These are the forms
				that Inventory Technicians and Support Staff see when they log
				into IRIS to inventory new assets or update existing assets.
			</p>
			<p class="mb-2" style="font-weight: bold">Remote forms</p>
			<p class="mb-2">
				Remote Forms are a very specific but less-used type of form
				designed to be exposed directly to end-users rather than IT
				staff. It is wizard driven, and has less functionality than a
				full inventory form.
			</p>

			<p class="mb-2">
				<span class="mb-2" style="font-weight: bold"> Form Types:</span>
				<br />
				<v-icon class="mr-1">mdi-account-group</v-icon>Remote form<br />
				<v-icon class="mr-1">mdi-account-tie</v-icon>Inventory form<br />
				<v-icon class="mr-1">mdi-form-textbox</v-icon>Steady-state form
			</p>
		</div>
		<v-container class="py-6" fluid>
			<v-row>
				<div style="flex-grow: 1">
					<v-text-field
						v-if="canView"
						v-model="search"
						class="py-0 my-0 mb-2 mr-2"
						append-icon="mdi-magnify"
						label="Search"
						single-line
						hide-details
					/>
				</div>
				<v-btn v-if="canAdd" color="primary" @click="addForm()">
					<v-icon> mdi-plus</v-icon>
					&nbsp;&nbsp;Add
				</v-btn>
			</v-row>
		</v-container>
		<div class="generic-grid-container">
			<v-data-table
				v-if="canView"
				dense
				:items="inventoryForms"
				:headers="headers"
				:search.sync="search"
				:loading="loading"
				disable-pagination
				fixed-header
				hide-default-footer
				loading-text="Loading..."
			>
				<template v-slot:[`item.Type`]="{ item }">
					{{ inventoryFormType(item) }}
				</template>

				<template v-slot:[`item.actions`]="{ item }">
					<v-btn
						v-if="canEdit"
						fab
						small
						icon
						elevation="1"
						class="action-btn"
						@click.stop="editForm(item)"
					>
						<v-icon color="primary" small>mdi-pencil</v-icon>
					</v-btn>
					<v-btn
						v-if="canDelete"
						fab
						small
						icon
						elevation="1"
						class="action-btn"
						@click.stop="deleteForm(item)"
					>
						<v-icon color="error" small>mdi-delete</v-icon>
					</v-btn>
					<v-btn
						v-if="canClone"
						fab
						small
						icon
						elevation="1"
						class="action-btn"
						@click.stop="cloneForm(item)"
					>
						<v-icon small>mdi-content-duplicate</v-icon>
					</v-btn>
				</template>
			</v-data-table>
		</div>
	</div>
</template>
<script>
import { mapGetters } from 'vuex';
import { FormBuilderActions } from '../../../store/actions';
import { FormBuilderGetters } from '../../../store/getters';
import { InventoryFormTypes } from '../../../enums/inventoryFormTypes';
import { Permissions } from '../../../enums/permissionsEnum';
import ConfirmDialog from '@/components/core/dialogs/confirmDialog';
import CloneDialog from '@/components/core/dialogs/cloneDialog';
import permissionsMixin from '../../../mixins/permissionsMixin';
export default {
	name: 'FormBuilder',
	mixins: [permissionsMixin],
	data() {
		return {
			search: '',
			headers: [
				{ text: 'Form Name', value: 'Name' },
				{ text: 'Form Type', value: 'Type' },
				{ text: '', value: 'actions', sortable: false, align: 'end' },
			],
		};
	},
	computed: {
		...mapGetters({
			inventoryForms: [FormBuilderGetters.FORMS],
			loading: [FormBuilderGetters.LOADING],
		}),
		canView() {
			return this.hasPermissions([Permissions.Tools.FormBuilder.View]);
		},
		canAdd() {
			return this.hasPermissions([Permissions.Tools.FormBuilder.Add]);
		},
		canEdit() {
			return this.hasPermissions([Permissions.Tools.FormBuilder.Edit]);
		},
		canDelete() {
			return this.hasPermissions([Permissions.Tools.FormBuilder.Delete]);
		},
		canClone() {
			return this.hasPermissions([Permissions.Tools.FormBuilder.Clone]);
		},
	},
	mounted() {
		this.$store.dispatch(FormBuilderActions.LOAD);
	},
	destroyed() {
		this.$store.dispatch(FormBuilderActions.CLEAR);
	},
	methods: {
		inventoryFormType(item) {
			switch (item.Type) {
				case InventoryFormTypes.Inventory:
					return 'Inventory';
				case InventoryFormTypes.RemoteUser:
					return 'Remote User';
				case InventoryFormTypes.SteadyState:
					return 'Steady State';
				default:
					return 'Text';
			}
		},
		addForm() {
			this.$router.push({ name: 'AddInventoryForm' });
		},

		editForm(item) {
			this.$router.push({
				name: 'EditInventoryForm',
				params: { id: item.Id },
			});
		},
		deleteForm(item) {
			ConfirmDialog({
				message: 'Are you sure you want to delete this form?',
			}).then((isDelete) => {
				if (!isDelete) return;
				this.$store.dispatch(FormBuilderActions.DELETE, item);
			});
		},
		cloneForm(item) {
			CloneDialog({
				message: item.Name,
			}).then((resp) => {
				if (!resp.success) return;

				let payload = {
					Name: resp.name,
					SourceFormId: item.Id,
				};

				this.$store.dispatch(FormBuilderActions.CLONE, payload);
			});
		},
	},
};
</script>
<style>
.text-container {
	height: 50%;
	overflow-y: scroll;
}
</style>
